import React from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { FormattedMessage } from 'react-intl';
// ui
import {
  FormControl,
  InputLabel,
  List, ListItem,
  ListItemSecondaryAction,
  ListItemText,
  MenuItem,
  Select,
  Switch,
  TextField,
} from '@material-ui/core';
import Slider from '@material-ui/core/Slider';

import styles from '../styles.module.css';
import * as actions from '../../../../state';
import BaseStyleControls from './BaseStyleControls';
import GaugeStyleControls from './GaugeStyleControls';
import GridTableStyleControls from './GridTableStyleControls';
import HeaderStyleControls from './HeaderStyleControls';
import LineStyleControls from './LineStyleControls';
import NoteStyleControls from './NoteStyleControls';
import StatBlockStyleControls from './StatBlockStyleControls';
import TableStyleControls from './TableStyleControls';

/**
 * helper function to traverse properties object by joined prop name [eg: properties.axisBottom.tickSize]
 * @param o
 * @param s
 * @returns {*}
 */
Object.byString = function byString(o, s) {
  // eslint-disable-next-line no-param-reassign
  s = s.replace(/\[(\w+)\]/g, '.$1'); // convert indexes to properties
  // eslint-disable-next-line no-param-reassign
  s = s.replace(/^\./, ''); // strip a leading dot
  const a = s.split('.');
  // eslint-disable-next-line no-plusplus
  for (let i = 0, n = a.length; i < n; ++i) {
    const k = a[i];
    if (k in o) {
      // eslint-disable-next-line no-param-reassign
      o = o[k];
    } else {
      return;
    }
  }
  // eslint-disable-next-line consistent-return
  return o;
};

function StyleControls() {
  const dispatch = useDispatch();
  const chart = useSelector(actions.readStateSelectedChart);

  // this recursive func handles multi-level props like "properties.theme.grid.strokeOpacity"
  const writeChartPropR = (chartChunk, nameChunks, value) => {
    const attrId = nameChunks.shift();

    if (!chartChunk[attrId]) { // if the prop isn't set yet, assign it an empty obj
      // eslint-disable-next-line no-param-reassign
      chartChunk[attrId] = {};
    }

    if (nameChunks.length >= 1) {
      writeChartPropR(chartChunk[attrId], nameChunks, value);
    } else {
      // eslint-disable-next-line no-param-reassign
      chartChunk[attrId] = value;
    }
  };

  // all updates should pass through here
  const updateChartProp = (name, value, renderRequired) => {
    chart.renderRequired = renderRequired || false;

    let val = value;
    // put any special value rules here
    if (name === 'properties.theme.axis.fontSize') {
      val += 'px';
    } // add 'px' unit, convert value to string
    if (name === 'properties.theme.grid.strokeOpacity') {
      val += '';
    } // convert value to string

    writeChartPropR(chart, name.split('.'), val);
    dispatch(actions.updateSelectedChart(chart));
  };

  const handleAttrChange = (e) => {
    const name = e.target.name === 'axisBottom.legend'
      ? `properties.${e.target.name}`
      : e.target.name;
    updateChartProp(name, e.target.value);
  };

  const handlePropChange = (e) => {
    const name = `properties.${e.target.name}`; // all changes go into properties attr
    updateChartProp(name, e.target.value);
  };

  const handleSwitchChange = (e, value) => {
    updateChartProp(`properties.${e.target.name}`, value);
  };

  const handleSliderChange = (e, name, value, renderRequired) => {
    // correct material ui slider value step bug
    let updatedVal = value;
    if (value.toString().includes('.')) {
      updatedVal = Number(value.toFixed(2));
    }

    updateChartProp(`properties.${name}`, updatedVal, renderRequired); // all changes go into properties attr
  };

  const renderControlItem = (controlItem, controlMsg, ciIndex, messages) => {
    const { name, type, renderRequired } = controlItem;
    const options = controlItem.options || [];

    let controlField;

    let propVal = Object.byString(chart.properties, name);
    if (typeof propVal === 'undefined') {
      propVal = controlItem.default;
    }

    switch (type) {
      /* case 'hidden':
        if (chart[name] !== propVal) {
          this.updateChartProp(name, propVal);
        }
        return ''; // don't draw */

      case 'text':
        controlField = (
          <TextField
            id={name}
            name={name}
            value={chart[name]}
            autoComplete="off"
            style={{ width: '100%' }}
            onChange={handleAttrChange}
            label={<FormattedMessage {...controlMsg} />}
            InputLabelProps={{
              classes: {
                root: styles.textFieldLabel,
              },
            }}
            InputProps={{
              className: styles.textFieldInput,
            }}
          />
        );
        break;

      case 'select':
        controlField = (
          <>
            <FormControl className={styles.selectControl}>
              <InputLabel shrink htmlFor="age-label-placeholder">
                <FormattedMessage {...controlMsg} />
              </InputLabel>

              <Select
                id={name}
                name={name}
                value={propVal}
                onChange={handlePropChange}
                classes={{
                  select: styles.sideSheetSelect,
                }}
              >
                {options.map((option) => {
                  let optionName = option.name || option;

                  if (option.labelId) optionName = <FormattedMessage {...messages[option.labelId]} />;

                  return (
                    <MenuItem
                      key={option.value || option}
                      value={option.value || option}
                    >
                      {optionName}
                    </MenuItem>
                  );
                })}
              </Select>
            </FormControl>
          </>
        );
        break;

      case 'slider':
        controlField = (
          <>
            <div className={styles.sliderTitle}>
              <FormattedMessage {...controlMsg} />

              <div className={styles.sliderInfo}>
                {propVal}
              </div>
            </div>

            <div className={styles.sliderContainer}>
              <Slider
                id={name}
                name={name}
                value={Number(propVal)}
                onChange={(event, value) => handleSliderChange(event, name, value, renderRequired)}
                min={options.min}
                max={options.max}
                step={options.step}
                aria-labelledby="label"
                className={styles.slider}
              />
            </div>
          </>
        );
        break;

      case 'switch':
        controlField = (
          <>
            <ListItemText primary={<FormattedMessage {...controlMsg} />} className={styles.switchTitle} />
            <ListItemSecondaryAction className={styles.switchBtn}>
              <Switch
                id={name}
                name={name}
                value={name}
                checked={Boolean(propVal)}
                className={styles.switchComponent}
                onChange={handleSwitchChange}
                classes={{
                  iconChecked: styles.secondaryColor,
                  bar: propVal ? styles.secondaryColorBg : '',
                }}
              />
            </ListItemSecondaryAction>
          </>
        );
        break;

      default:
        // nunya
        break;
    }

    return controlField ? (
      <ListItem
        key={ciIndex}
        className={type === 'switch' ? styles.menuListItemDense : styles.menuListItem}
      >
        {controlField}
      </ListItem>
    ) : '';
  };

  const renderControlGroup = (controlGroup, messages, cgIndex) => (
    <List key={cgIndex} dense className={styles.menu}>
      <ListItem className={styles.menuItemHeader}>
        <ListItemText
          primary={<FormattedMessage {...messages[controlGroup.labelId]} />}
        />
      </ListItem>

      {controlGroup.controls.map((controlItem, ciIndex) => renderControlItem(
        controlItem, messages[controlItem.labelId], ciIndex, messages,
      ))}
    </List>
  );

  let styleControls;
  switch (chart.type) {
    case 'Header':
      styleControls = (
        <HeaderStyleControls renderControlItem={renderControlItem} />
      );
      break;
    case 'ResponsiveGauge':
      styleControls = (
        <GaugeStyleControls renderControlGroup={renderControlGroup} />
      );
      break;
    case 'ResponsiveLine':
      styleControls = (
        <LineStyleControls renderControlGroup={renderControlGroup} />
      );
      break;
    case 'ResponsiveNote':
      styleControls = (
        <NoteStyleControls renderControlGroup={renderControlGroup} />
      );
      break;
    case 'ResponsiveStat':
      styleControls = (
        <StatBlockStyleControls renderControlGroup={renderControlGroup} />
      );
      break;
    case 'ResponsiveTable':
      styleControls = (
        <TableStyleControls renderControlGroup={renderControlGroup} />
      );
      break;
    case 'ResponsiveGridTable':
      styleControls = (
        <GridTableStyleControls renderControlGroup={renderControlGroup} />
      );
      break;
    default:
      // nothing
      break;
  }

  return chart.type !== 'Header' ? (
    <List className={styles.controlsContainer} dense disablePadding>
      <BaseStyleControls renderControlGroup={renderControlGroup} />
      {styleControls}
    </List>
  ) : (
    <List className={styles.controlsContainerNoTab} dense disablePadding>
      {styleControls}
    </List>
  );
}

export default StyleControls;
